import { useCallback } from "react";

import { useGate } from "@nestoca/gate-react";
import { useTenantSetting } from "@nestoca/multi-tenant";
import { Applicant, Application } from "@shared/constants";
import { getMainApplicant, getCoApplicantsByRole } from "@shared/utils";

import { ApplicationTableItem } from "@components/dashboard-table/types";

const getApplicantName = (applicant?: Applicant) => {
  if (!applicant) return "";
  return `${applicant.firstName} ${applicant.lastName}`;
};

// Transform application data to a format that can be used by the table
export const useApplicationTransformer = () => {
  const gate = useGate();
  const role = gate.getRole();
  const isExternalBroker = role === "externalbroker";

  const { value: isLoanDetailsEnabled } = useTenantSetting("loanDetails");

  return useCallback(
    (application: Application): ApplicationTableItem => {
      const mainApplicant = getMainApplicant(application);
      const mainApplicantName = getApplicantName(mainApplicant);

      const standardApplication = {
        id: application.id,
        tenantReferralId: application.referralSnapshot?.tenantReferralId,
        isDigital: !application.isNonDigital,
        name: mainApplicantName,
        transactionType: application.type,
        startingDate: application.created,
        currentStatus: application.applicationState,
        closedLostReason: application.closedLostReason,
        coApplicantsByRole: getCoApplicantsByRole(application),
        contactMade: application.contactMade,
      };

      const shouldHideLegacyFields = isLoanDetailsEnabled && !isExternalBroker;

      if (shouldHideLegacyFields) {
        return standardApplication;
      }

      const mortgageAmount =
        application.mortgage?.mortgageAmountPremiumsIncluded || 0;
      const helocAmount = application.heloc?.helocAmount || 0;

      return {
        ...standardApplication,
        productType: {
          type: application.product?.type,
          term: application.product?.term,
        },
        loanValue: mortgageAmount + helocAmount,
        rate: application.mortgage?.finalRate,
        heloc: application.heloc,
        closingDate: application.mortgage?.closingDate,
        mortgageAmountPremiumsIncluded:
          application.mortgage?.mortgageAmountPremiumsIncluded,
      };
    },
    [isLoanDetailsEnabled, isExternalBroker]
  );
};
